import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import Button from './Button';
import CloseIcon from '../icons/CloseIcon';
import SearchIcon from '../icons/SearchIcon';

interface ClassStudentRow {
  uid: string;
  name: string;
  studentNumber?: number;
}

interface AwardAchievementToStudentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  achievementId: string;
  achievementTitle: string;
  schoolId: string;
  classId: string;
  teacherId: string;
}

const AwardAchievementToStudentsDialog: React.FC<AwardAchievementToStudentsDialogProps> = ({
  isOpen,
  onClose,
  achievementId,
  achievementTitle,
  schoolId,
  classId,
  teacherId
}) => {
  const [students, setStudents] = useState<ClassStudentRow[]>([]);
  const [awardedIds, setAwardedIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !achievementId) return;

    setLoading(true);
    setSelectedIds(new Set());
    setSearch('');

    const studentsRef = collection(db, `schools/${schoolId}/classes/${classId}/students`);
    const awardsRef = collection(
      db,
      'schools',
      schoolId,
      'classes',
      classId,
      'achievements',
      achievementId,
      'awards'
    );

    let cancelled = false;

    const loadStudents = async () => {
      try {
        const snap = await getDocs(studentsRef);
        if (cancelled) return;
        const rows: ClassStudentRow[] = snap.docs.map((d) => {
          const data = d.data() as { name?: string; studentNumber?: number };
          return {
            uid: d.id,
            name: data.name || '이름 없음',
            studentNumber: data.studentNumber
          };
        });
        rows.sort((a, b) => {
          const na = a.studentNumber ?? 999;
          const nb = b.studentNumber ?? 999;
          if (na !== nb) return na - nb;
          return a.name.localeCompare(b.name, 'ko');
        });
        setStudents(rows);
      } catch (e) {
        console.error('학생 목록 로드 오류:', e);
        setStudents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStudents();

    const unsubAwards = onSnapshot(awardsRef, (snapshot) => {
      const ids = new Set(snapshot.docs.map((d) => d.id));
      setAwardedIds(ids);
    });

    return () => {
      cancelled = true;
      unsubAwards();
    };
  }, [isOpen, achievementId, schoolId, classId]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        String(s.studentNumber ?? '').includes(q)
    );
  }, [students, search]);

  const toggleSelect = (uid: string) => {
    if (awardedIds.has(uid)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleAwardSelected = async () => {
    const toAward = [...selectedIds].filter((id) => !awardedIds.has(id));
    if (toAward.length === 0) return;

    setSaving(true);
    try {
      const batch = writeBatch(db);
      for (const studentId of toAward) {
        const ref = doc(
          db,
          'schools',
          schoolId,
          'classes',
          classId,
          'achievements',
          achievementId,
          'awards',
          studentId
        );
        batch.set(ref, {
          studentId,
          teacherId,
          awardedAt: serverTimestamp()
        });
      }
      await batch.commit();
      setSelectedIds(new Set());
    } catch (e) {
      console.error('업적 부여 오류:', e);
      alert('업적 부여 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (studentId: string) => {
    if (!window.confirm('이 학생에게서 업적 부여를 취소할까요?')) return;
    setSaving(true);
    try {
      await deleteDoc(
        doc(
          db,
          'schools',
          schoolId,
          'classes',
          classId,
          'achievements',
          achievementId,
          'awards',
          studentId
        )
      );
    } catch (e) {
      console.error('업적 취소 오류:', e);
      alert('취소 처리 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100000
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(440px, calc(100vw - 32px))',
          maxHeight: 'min(640px, calc(100vh - 48px))',
          background: 'var(--Color-background-normal, white)',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 20px 12px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 600,
                fontFamily: 'Pretendard',
                color: 'var(--Color-text-strong, #0D0D0D)',
                lineHeight: '27px'
              }}
            >
              학생에게 부여
            </div>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: 'Pretendard',
                color: 'var(--Color-text-normal, #161616)',
                lineHeight: '19.5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={achievementTitle}
            >
              {achievementTitle}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '4px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: '8px',
              flexShrink: 0
            }}
            aria-label="닫기"
          >
            <CloseIcon width={24} height={24} color="#0D0D0D" />
          </button>
        </div>

        <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid var(--Color-line-normal, #F0F0F0)'
            }}
          >
            <SearchIcon width={18} height={18} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="이름 또는 번호 검색"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                fontFamily: 'Pretendard',
                background: 'transparent'
              }}
            />
          </div>
          <div style={{ fontSize: '12px', color: 'var(--Color-text-assistive, #989898)' }}>
            부여됨 {awardedIds.size}명 · 선택 {selectedIds.size}명
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0 12px 12px 20px',
            minHeight: '200px'
          }}
        >
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', fontSize: '14px', color: '#989898' }}>
              불러오는 중…
            </div>
          ) : filteredStudents.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', fontSize: '14px', color: '#989898' }}>
              학생이 없습니다.
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {filteredStudents.map((s) => {
                const awarded = awardedIds.has(s.uid);
                const checked = selectedIds.has(s.uid);
                return (
                  <li
                    key={s.uid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '10px 8px',
                      borderRadius: '8px',
                      background: awarded ? 'var(--Color-fill-hover, #F5F5F5)' : 'transparent'
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        cursor: awarded ? 'default' : 'pointer',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={awarded || saving}
                        onChange={() => toggleSelect(s.uid)}
                        style={{ width: '18px', height: '18px', flexShrink: 0 }}
                      />
                      <span
                        style={{
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: 500,
                          color: 'var(--Color-text-normal, #161616)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {s.studentNumber != null ? `${s.studentNumber}번 ` : ''}
                        {s.name}
                      </span>
                    </label>
                    {awarded ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: 'var(--Color-fill-brand-color, #FF6F0F)'
                          }}
                        >
                          부여됨
                        </span>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleRevoke(s.uid)}
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid var(--Color-line-normal, #E0E0E0)',
                            background: 'white',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            color: 'var(--Color-text-assistive-strong, #4C4C4C)'
                          }}
                        >
                          취소
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}
        >
          <Button variant="cancel" height="44px" onClick={onClose} disabled={saving}>
            닫기
          </Button>
          <Button
            variant="submit"
            height="44px"
            onClick={handleAwardSelected}
            disabled={saving || [...selectedIds].filter((id) => !awardedIds.has(id)).length === 0}
          >
            {saving ? '처리 중…' : '선택한 학생에게 부여'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AwardAchievementToStudentsDialog;
